import { COLS, ROWS, type TdMap } from "../../shared/games/taarn";

/**
 * Flat (2D) scenery for the four new maps – used when WebGL is not available. The 3D worlds live in td3d/themes.ts; this is the same
 * idea drawn with canvas: coloured ground, a road in the map's own material and a handful of props that tell the theme at a glance.
 */
const CELL = 64;
const W = COLS * CELL;
const H = ROWS * CELL;
const hash = (n: number) => { const x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x); };

type Ctx = CanvasRenderingContext2D;
interface Geo { pts: readonly (readonly [number, number])[]; dist: (x: number, y: number) => number }

function rr(c: Ctx, x: number, y: number, w: number, h: number, r: number) {
  c.beginPath();
  c.moveTo(x + r, y);
  c.arcTo(x + w, y, x + w, y + h, r);
  c.arcTo(x + w, y + h, x, y + h, r);
  c.arcTo(x, y + h, x, y, r);
  c.arcTo(x, y, x + w, y, r);
  c.closePath();
}
function stroke(c: Ctx, pts: Geo["pts"], w: number, col: string | CanvasGradient) {
  c.strokeStyle = col; c.lineWidth = w; c.lineJoin = "round"; c.lineCap = "round";
  c.beginPath();
  pts.forEach(([x, y], i) => (i ? c.lineTo(x, y) : c.moveTo(x, y)));
  c.stroke();
}
const shadow = (c: Ctx, x: number, y: number, rx: number, ry: number, a = 0.25) => { c.fillStyle = `rgba(0,0,0,${a})`; c.beginPath(); c.ellipse(x, y, rx, ry, 0, 0, 7); c.fill(); };

/* ───────────── props ───────────── */
function palm(c: Ctx, x: number, y: number, s: number, seed: number) {
  shadow(c, x + s * 0.2, y + s * 0.15, s * 0.6, s * 0.18);
  c.strokeStyle = "#8a5a2b"; c.lineWidth = s * 0.16; c.lineCap = "round";
  c.beginPath(); c.moveTo(x, y); c.quadraticCurveTo(x + s * 0.2, y - s * 0.6, x + s * 0.05, y - s * 1.1); c.stroke();
  const tx = x + s * 0.05, ty = y - s * 1.1;
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2 + hash(seed) * 2;
    c.fillStyle = i % 2 ? "#2f9a45" : "#4cba55"; c.strokeStyle = "#1d6a30"; c.lineWidth = 1.2;
    c.save(); c.translate(tx, ty); c.rotate(a);
    c.beginPath(); c.moveTo(0, 0); c.quadraticCurveTo(s * 0.5, -s * 0.28, s * 0.95, s * 0.12); c.quadraticCurveTo(s * 0.5, s * 0.02, 0, 0); c.fill(); c.stroke();
    c.restore();
  }
  c.fillStyle = "#8a5a2b"; c.beginPath(); c.arc(tx - 3, ty + 3, 3.4, 0, 7); c.arc(tx + 4, ty + 4, 3.4, 0, 7); c.fill();
}
function cactus(c: Ctx, x: number, y: number, s: number) {
  shadow(c, x + 3, y + 2, s * 0.5, s * 0.14);
  c.fillStyle = "#3fae4e"; c.strokeStyle = "#1d6a30"; c.lineWidth = 2;
  rr(c, x - s * 0.17, y - s, s * 0.34, s, s * 0.17); c.fill(); c.stroke();
  rr(c, x - s * 0.55, y - s * 0.62, s * 0.34, s * 0.1, s * 0.05); c.fill(); rr(c, x - s * 0.55, y - s * 0.86, s * 0.2, s * 0.34, s * 0.1); c.fill(); c.stroke();
  rr(c, x + s * 0.21, y - s * 0.5, s * 0.32, s * 0.1, s * 0.05); c.fill(); rr(c, x + s * 0.34, y - s * 0.78, s * 0.2, s * 0.38, s * 0.1); c.fill(); c.stroke();
  c.fillStyle = "#ff7ab0"; c.beginPath(); c.arc(x, y - s, 3, 0, 7); c.fill();
}
function dune(c: Ctx, x: number, y: number, s: number) {
  c.strokeStyle = "rgba(160,110,50,.35)"; c.lineWidth = 2; c.lineCap = "round";
  for (let k = 0; k < 3; k++) { c.beginPath(); c.arc(x, y + k * 7 + 22, s * (1 - k * 0.2), Math.PI * 1.15, Math.PI * 1.85); c.stroke(); }
}
function pyramid(c: Ctx, x: number, y: number, s: number) {
  shadow(c, x + s * 0.2, y + s * 0.1, s * 0.9, s * 0.18);
  c.strokeStyle = "#7a4a14"; c.lineWidth = 2.4;
  c.fillStyle = "#f2c66b"; c.beginPath(); c.moveTo(x - s * 0.8, y); c.lineTo(x, y - s * 0.9); c.lineTo(x + s * 0.2, y); c.closePath(); c.fill(); c.stroke();
  c.fillStyle = "#cf9a3d"; c.beginPath(); c.moveTo(x + s * 0.2, y); c.lineTo(x, y - s * 0.9); c.lineTo(x + s * 0.85, y); c.closePath(); c.fill(); c.stroke();
}
function pine(c: Ctx, x: number, y: number, s: number, snow = true) {
  shadow(c, x + 3, y + 2, s * 0.5, s * 0.16);
  c.fillStyle = "#6d4a2a"; c.fillRect(x - s * 0.07, y - s * 0.2, s * 0.14, s * 0.25);
  for (let k = 0; k < 3; k++) {
    const w = s * (0.62 - k * 0.14), yy = y - s * 0.15 - k * s * 0.3;
    c.fillStyle = "#2c7d52"; c.strokeStyle = "#16452c"; c.lineWidth = 1.6;
    c.beginPath(); c.moveTo(x - w, yy); c.lineTo(x, yy - s * 0.46); c.lineTo(x + w, yy); c.closePath(); c.fill(); c.stroke();
    if (snow) { c.fillStyle = "#fff"; c.beginPath(); c.moveTo(x - w * 0.62, yy - s * 0.17); c.lineTo(x, yy - s * 0.46); c.lineTo(x + w * 0.62, yy - s * 0.17); c.quadraticCurveTo(x + w * 0.2, yy - s * 0.1, x, yy - s * 0.17); c.quadraticCurveTo(x - w * 0.3, yy - s * 0.08, x - w * 0.62, yy - s * 0.17); c.fill(); }
  }
}
function snowman(c: Ctx, x: number, y: number, s: number) {
  shadow(c, x + 2, y + 2, s * 0.5, s * 0.14);
  c.fillStyle = "#fff"; c.strokeStyle = "#8aa4c4"; c.lineWidth = 1.6;
  c.beginPath(); c.arc(x, y - s * 0.28, s * 0.3, 0, 7); c.fill(); c.stroke();
  c.beginPath(); c.arc(x, y - s * 0.7, s * 0.22, 0, 7); c.fill(); c.stroke();
  c.fillStyle = "#222"; c.beginPath(); c.arc(x - 3, y - s * 0.74, 1.4, 0, 7); c.arc(x + 3, y - s * 0.74, 1.4, 0, 7); c.fill();
  c.fillStyle = "#ff8a1f"; c.beginPath(); c.moveTo(x, y - s * 0.7); c.lineTo(x + s * 0.28, y - s * 0.66); c.lineTo(x, y - s * 0.64); c.fill();
  c.fillStyle = "#e0483c"; c.fillRect(x - s * 0.22, y - s * 0.52, s * 0.44, s * 0.07);
  c.fillStyle = "#333"; c.fillRect(x - s * 0.2, y - s * 0.94, s * 0.4, s * 0.08); c.fillRect(x - s * 0.13, y - s * 1.1, s * 0.26, s * 0.18);
}
function iceCrystal(c: Ctx, x: number, y: number, s: number) {
  shadow(c, x + 2, y + 2, s * 0.5, s * 0.14, 0.18);
  const g = c.createLinearGradient(x - s, y - s, x + s, y);
  g.addColorStop(0, "#e8fbff"); g.addColorStop(1, "#6fd0ff");
  c.fillStyle = g; c.strokeStyle = "#3a8fc9"; c.lineWidth = 1.6;
  for (const [dx, h, w] of [[-s * 0.3, s * 0.7, s * 0.2], [0, s, s * 0.26], [s * 0.3, s * 0.55, s * 0.18]] as const) {
    c.beginPath(); c.moveTo(x + dx - w, y); c.lineTo(x + dx - w * 0.3, y - h); c.lineTo(x + dx + w * 0.4, y - h * 0.9); c.lineTo(x + dx + w, y); c.closePath(); c.fill(); c.stroke();
  }
}
function lavaCrack(c: Ctx, x: number, y: number, s: number, seed: number) {
  c.lineCap = "round"; c.lineJoin = "round";
  let px = x, py = y;
  const path: [number, number][] = [[px, py]];
  for (let i = 0; i < 5; i++) { px += (hash(seed + i) - 0.3) * s * 0.7; py += (hash(seed * 3 + i) - 0.5) * s * 0.5; path.push([px, py]); }
  for (const [w, col] of [[7, "rgba(255,90,20,.25)"], [3.6, "#ff7a1f"], [1.6, "#ffe066"]] as const) {
    c.strokeStyle = col; c.lineWidth = w; c.beginPath(); path.forEach(([a, b], i) => (i ? c.lineTo(a, b) : c.moveTo(a, b))); c.stroke();
  }
}
function volcanoRock(c: Ctx, x: number, y: number, s: number) {
  shadow(c, x + 2, y + s * 0.4, s * 1.1, s * 0.3, 0.3);
  const g = c.createLinearGradient(x - s, y - s, x + s, y + s); g.addColorStop(0, "#5a4f60"); g.addColorStop(1, "#1e1a24");
  c.fillStyle = g; c.strokeStyle = "#100d14"; c.lineWidth = 1.8;
  c.beginPath(); c.moveTo(x - s, y + s * 0.4); c.lineTo(x - s * 0.7, y - s * 0.45); c.lineTo(x - s * 0.05, y - s * 0.85); c.lineTo(x + s * 0.8, y - s * 0.35); c.lineTo(x + s, y + s * 0.4); c.closePath(); c.fill(); c.stroke();
  c.strokeStyle = "#ff7a1f"; c.lineWidth = 1.4; c.beginPath(); c.moveTo(x - s * 0.2, y - s * 0.7); c.lineTo(x - s * 0.1, y - s * 0.1); c.stroke();
}
function lollipop(c: Ctx, x: number, y: number, s: number, seed: number) {
  shadow(c, x + 3, y + 2, s * 0.45, s * 0.13);
  c.strokeStyle = "#fff"; c.lineWidth = 3.6; c.beginPath(); c.moveTo(x, y); c.lineTo(x, y - s * 0.7); c.stroke();
  c.strokeStyle = "#e9a6c4"; c.lineWidth = 1; c.stroke();
  const cols = [["#ff5fa8", "#ffd1e6"], ["#43d6c4", "#d6fff9"], ["#ffb02e", "#fff0c2"], ["#8f6bff", "#e4daff"]][Math.floor(hash(seed) * 4)]!;
  const cx = x, cy = y - s * 0.95, r = s * 0.34;
  c.fillStyle = cols[1]!; c.strokeStyle = "#7a2a5a"; c.lineWidth = 1.8; c.beginPath(); c.arc(cx, cy, r, 0, 7); c.fill(); c.stroke();
  c.strokeStyle = cols[0]!; c.lineWidth = 3.4; c.beginPath();
  for (let a = 0; a < 15; a += 0.2) { const rr2 = (a / 15) * r * 0.9; const px = cx + Math.cos(a) * rr2, py = cy + Math.sin(a) * rr2; if (a === 0) c.moveTo(px, py); else c.lineTo(px, py); }
  c.stroke();
}
function candyCane(c: Ctx, x: number, y: number, s: number) {
  shadow(c, x + 3, y + 2, s * 0.4, s * 0.12);
  c.lineCap = "round";
  for (const [w, col] of [[7, "#fff"], [7, "#e0483c"]] as const) {
    c.strokeStyle = col; c.lineWidth = w; if (col !== "#fff") c.setLineDash([5, 8]);
    c.beginPath(); c.moveTo(x, y); c.lineTo(x, y - s * 0.75); c.arc(x + s * 0.2, y - s * 0.75, s * 0.2, Math.PI, 0); c.stroke(); c.setLineDash([]);
  }
}
function cupcake(c: Ctx, x: number, y: number, s: number, seed: number) {
  shadow(c, x + 2, y + 2, s * 0.5, s * 0.14);
  c.fillStyle = "#e8a85a"; c.strokeStyle = "#7a4a1a"; c.lineWidth = 1.8;
  c.beginPath(); c.moveTo(x - s * 0.34, y - s * 0.34); c.lineTo(x + s * 0.34, y - s * 0.34); c.lineTo(x + s * 0.24, y); c.lineTo(x - s * 0.24, y); c.closePath(); c.fill(); c.stroke();
  const fr = ["#ff8fc2", "#8fe8ff", "#ffe066"][Math.floor(hash(seed) * 3)]!;
  c.fillStyle = fr; c.beginPath(); c.arc(x, y - s * 0.52, s * 0.34, Math.PI, 0); c.arc(x, y - s * 0.78, s * 0.22, Math.PI, 0); c.fill(); c.stroke();
  c.fillStyle = "#e0483c"; c.beginPath(); c.arc(x, y - s * 1.02, s * 0.1, 0, 7); c.fill();
}
function sprinkle(c: Ctx, x: number, y: number, seed: number) {
  c.lineCap = "round"; c.lineWidth = 3;
  c.strokeStyle = ["#ff5fa8", "#43d6c4", "#ffb02e", "#8f6bff", "#fff"][Math.floor(hash(seed) * 5)]!;
  const a = hash(seed + 1) * 3.14;
  c.beginPath(); c.moveTo(x, y); c.lineTo(x + Math.cos(a) * 6, y + Math.sin(a) * 6); c.stroke();
}

/* ───────────── start & goal ───────────── */
function startGate(c: Ctx, cy: number, id: TdMap["id"]) {
  const x = 0;
  if (id === "oerken") { // the pyramid door
    shadow(c, x + 20, cy + 40, 60, 12);
    c.fillStyle = "#e8b65a"; c.strokeStyle = "#7a4a14"; c.lineWidth = 3;
    c.beginPath(); c.moveTo(-6, cy + 44); c.lineTo(-6, cy - 38); c.lineTo(34, cy - 22); c.lineTo(52, cy + 44); c.closePath(); c.fill(); c.stroke();
    c.fillStyle = "#2a1608"; rr(c, -8, cy - 26, 32, 52, 12); c.fill();
    c.fillStyle = "#2bb7a6"; c.fillRect(-8, cy - 30, 32, 5);
  } else if (id === "vinter") { // igloo
    shadow(c, x + 24, cy + 40, 56, 12, 0.2);
    c.fillStyle = "#f4fbff"; c.strokeStyle = "#6a93bb"; c.lineWidth = 3;
    c.beginPath(); c.arc(8, cy + 12, 48, Math.PI, 0); c.lineTo(56, cy + 36); c.lineTo(-40, cy + 36); c.closePath(); c.fill(); c.stroke();
    c.strokeStyle = "rgba(106,147,187,.7)"; c.lineWidth = 1.4; for (const dy of [-18, -4, 10]) { c.beginPath(); c.moveTo(-30, cy + dy); c.lineTo(46, cy + dy); c.stroke(); }
    c.fillStyle = "#1d3b63"; rr(c, -8, cy - 18, 30, 52, 14); c.fill();
  } else if (id === "vulkan") { // crater mouth
    shadow(c, x + 24, cy + 40, 58, 12, 0.3);
    const g = c.createLinearGradient(0, cy - 40, 0, cy + 44); g.addColorStop(0, "#4a3f52"); g.addColorStop(1, "#1d1822");
    c.fillStyle = g; c.strokeStyle = "#0e0b12"; c.lineWidth = 3;
    c.beginPath(); c.moveTo(-8, cy + 44); c.lineTo(-8, cy - 44); c.lineTo(30, cy - 30); c.lineTo(54, cy + 44); c.closePath(); c.fill(); c.stroke();
    const lg = c.createRadialGradient(8, cy, 2, 8, cy, 34); lg.addColorStop(0, "#fff2a8"); lg.addColorStop(0.4, "#ff7a1f"); lg.addColorStop(1, "#7a1a08");
    c.fillStyle = lg; rr(c, -8, cy - 28, 38, 56, 18); c.fill();
  } else { // cake house
    shadow(c, x + 22, cy + 42, 56, 12, 0.2);
    c.fillStyle = "#f6c789"; c.strokeStyle = "#7a4a1a"; c.lineWidth = 3; rr(c, -14, cy - 24, 62, 66, 6); c.fill(); c.stroke();
    c.fillStyle = "#ff8fc2"; c.beginPath(); c.moveTo(-18, cy - 20); for (let k = 0; k <= 5; k++) c.quadraticCurveTo(-18 + k * 13 + 3, cy - 4, -18 + (k + 1) * 13, cy - 20); c.lineTo(50, cy - 34); c.lineTo(-18, cy - 34); c.closePath(); c.fill(); c.stroke();
    c.fillStyle = "#7a3a2a"; rr(c, -8, cy - 4, 26, 46, 12); c.fill();
  }
}
function goal(c: Ctx, bx: number, by: number, id: TdMap["id"]) {
  const cx = bx * CELL + CELL / 2 - 6, cy = by * CELL + CELL / 2 + 4;
  shadow(c, cx + 6, cy + 32, 48, 13, 0.28);
  c.lineWidth = 3;
  if (id === "oerken") { // golden temple
    c.strokeStyle = "#6a3d15";
    const g = c.createLinearGradient(cx - 30, 0, cx + 30, 0); g.addColorStop(0, "#fff0a8"); g.addColorStop(1, "#d99a1e");
    c.fillStyle = g;
    rr(c, cx - 30, cy - 10, 60, 40, 3); c.fill(); c.stroke();
    c.beginPath(); c.moveTo(cx - 38, cy - 10); c.lineTo(cx, cy - 44); c.lineTo(cx + 38, cy - 10); c.closePath(); c.fill(); c.stroke();
    c.fillStyle = "#3a1d08"; rr(c, cx - 8, cy + 6, 16, 24, 7); c.fill();
    c.fillStyle = "#2bb7a6"; c.beginPath(); c.arc(cx, cy - 22, 5, 0, 7); c.fill(); c.stroke();
  } else if (id === "vinter") { // ice palace
    c.strokeStyle = "#2f5f93";
    const g = c.createLinearGradient(0, cy - 60, 0, cy + 30); g.addColorStop(0, "#f4fdff"); g.addColorStop(1, "#8fd6ff");
    c.fillStyle = g;
    for (const [dx, h] of [[-26, 40], [26, 40], [0, 62]] as const) { c.beginPath(); c.moveTo(cx + dx - 13, cy + 28); c.lineTo(cx + dx - 13, cy - h + 14); c.lineTo(cx + dx, cy - h - 12); c.lineTo(cx + dx + 13, cy - h + 14); c.lineTo(cx + dx + 13, cy + 28); c.closePath(); c.fill(); c.stroke(); }
    c.fillStyle = "#1d3b63"; rr(c, cx - 8, cy + 6, 16, 22, 8); c.fill();
  } else if (id === "vulkan") { // tiki temple
    c.strokeStyle = "#2a1408";
    const g = c.createLinearGradient(cx - 30, 0, cx + 30, 0); g.addColorStop(0, "#c98a4a"); g.addColorStop(1, "#7a4a22");
    c.fillStyle = g; rr(c, cx - 26, cy - 40, 52, 70, 6); c.fill(); c.stroke();
    c.fillStyle = "#ff5a1f"; c.beginPath(); c.moveTo(cx - 30, cy - 40); c.lineTo(cx, cy - 66); c.lineTo(cx + 30, cy - 40); c.closePath(); c.fill(); c.stroke();
    c.fillStyle = "#fff"; c.beginPath(); c.arc(cx - 10, cy - 16, 7, 0, 7); c.arc(cx + 10, cy - 16, 7, 0, 7); c.fill(); c.stroke();
    c.fillStyle = "#ff3a14"; c.beginPath(); c.arc(cx - 10, cy - 16, 3, 0, 7); c.arc(cx + 10, cy - 16, 3, 0, 7); c.fill();
    c.fillStyle = "#2a1408"; rr(c, cx - 12, cy + 2, 24, 14, 5); c.fill();
    c.fillStyle = "#fff"; for (let k = -2; k <= 2; k++) c.fillRect(cx + k * 4.4 - 1.6, cy + 3, 3.2, 5);
  } else { // cupcake castle
    c.strokeStyle = "#7a2a5a";
    c.fillStyle = "#f6c789"; c.beginPath(); c.moveTo(cx - 34, cy + 30); c.lineTo(cx - 26, cy - 8); c.lineTo(cx + 26, cy - 8); c.lineTo(cx + 34, cy + 30); c.closePath(); c.fill(); c.stroke();
    c.fillStyle = "#ff8fc2"; c.beginPath(); c.arc(cx, cy - 12, 34, Math.PI, 0); c.fill(); c.stroke();
    c.fillStyle = "#fff"; c.beginPath(); c.arc(cx, cy - 36, 20, Math.PI, 0); c.fill(); c.stroke();
    c.fillStyle = "#e0483c"; c.beginPath(); c.arc(cx, cy - 58, 7, 0, 7); c.fill(); c.stroke();
    c.fillStyle = "#7a3a2a"; rr(c, cx - 9, cy + 6, 18, 24, 9); c.fill();
  }
}

/* ───────────── the map itself ───────────── */
export function paintThemed(c: Ctx, map: TdMap, geo: Geo) {
  const p = map.def.palette;
  const id = map.id;
  const g = c.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, p.ground); g.addColorStop(1, p.ground2);
  c.fillStyle = g; c.fillRect(0, 0, W, H);
  for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
    const v = hash(x * 31 + y * 57 + id.length);
    c.fillStyle = (x + y) % 2 ? "rgba(255,255,255,.035)" : "rgba(0,0,0,.03)";
    c.fillRect(x * CELL, y * CELL, CELL, CELL);
    c.fillStyle = v > 0.5 ? `rgba(255,255,255,${0.06 * v})` : `rgba(0,0,0,${0.06 * (1 - v)})`;
    c.fillRect(x * CELL, y * CELL, CELL, CELL);
  }
  for (let i = 0; i < 30; i++) {
    const px = hash(i * 7.3) * W, py = hash(i * 3.1 + 9) * H, r = 80 + hash(i) * 130;
    const rg = c.createRadialGradient(px, py, 0, px, py, r);
    rg.addColorStop(0, i % 2 === 0 ? "rgba(255,255,255,.2)" : "rgba(0,0,0,.14)"); rg.addColorStop(1, "rgba(0,0,0,0)");
    c.fillStyle = rg; c.fillRect(px - r, py - r, r * 2, r * 2);
  }
  const spots = (n: number, seed: number, minD: number, f: (x: number, y: number, i: number) => void) => {
    for (let i = 0; i < n; i++) { const x = hash(i * 3.7 + seed) * W, y = hash(i * 5.9 + seed * 2) * H; if (geo.dist(x, y) > minD) f(x, y, i); }
  };
  // ground texture
  if (id === "oerken") spots(40, 3, CELL * 0.7, (x, y) => dune(c, x, y, 20 + hash(x) * 14));
  if (id === "vulkan") spots(34, 5, CELL * 0.75, (x, y, i) => lavaCrack(c, x, y, 40 + hash(i) * 40, i));
  if (id === "slik") spots(260, 7, CELL * 0.62, (x, y, i) => sprinkle(c, x, y, i));
  if (id === "vinter") spots(160, 9, CELL * 0.6, (x, y, i) => { c.fillStyle = "rgba(255,255,255,.9)"; c.beginPath(); c.arc(x, y, 1.2 + hash(i) * 1.8, 0, 7); c.fill(); });
  // pond / oasis / lava pool
  { const px = 62, py = H - 50;
    const water: [string, string, string] = id === "oerken" ? ["#a8fff0", "#1fb6b0", "#e8c27a"] : id === "vinter" ? ["#f2fcff", "#9fdcff", "#cfe6f5"] : id === "vulkan" ? ["#fff2a8", "#ff5a1f", "#2a1d24"] : ["#6b3b1f", "#3b1d0c", "#fff4b8"];
    c.fillStyle = water[2]; c.beginPath(); c.ellipse(px, py, 80, 47, -0.1, 0, 7); c.fill();
    const wg = c.createLinearGradient(px, py - 40, px, py + 40); wg.addColorStop(0, water[0]); wg.addColorStop(1, water[1]);
    c.fillStyle = wg; c.beginPath(); c.ellipse(px, py, 70, 39, -0.1, 0, 7); c.fill();
    c.strokeStyle = "rgba(255,255,255,.7)"; c.lineWidth = 2; c.beginPath(); c.ellipse(px - 10, py - 6, 26, 8, 0, 3.4, 5.6); c.stroke();
  }
  // road: wide soft edge, kerb, surface, centre accent
  stroke(c, geo.pts, CELL * 1.16, "rgba(0,0,0,.2)");
  stroke(c, geo.pts, CELL * 1.0, p.road2);
  stroke(c, geo.pts, CELL * 0.88, p.road);
  if (id === "slik" || id === "vinter") { c.setLineDash([14, 14]); stroke(c, geo.pts, 4, id === "slik" ? "#fff" : "rgba(255,255,255,.8)"); c.setLineDash([]); }
  if (id === "vulkan") stroke(c, geo.pts, 5, "rgba(255,110,30,.55)");
  for (let i = 0; i < 480; i++) { // grain on the road
    const x = hash(i * 1.9 + 3) * W, y = hash(i * 2.7 + 8) * H;
    if (geo.dist(x, y) > CELL * 0.4) continue;
    c.fillStyle = hash(i) > 0.5 ? "rgba(255,255,255,.18)" : "rgba(0,0,0,.14)";
    c.beginPath(); c.ellipse(x, y, 2 + hash(i + 1) * 4, 1.4 + hash(i + 2) * 2.4, hash(i) * 3, 0, 7); c.fill();
  }
  // props
  if (id === "oerken") {
    spots(14, 21, CELL * 0.85, (x, y, i) => cactus(c, x, y, 26 + hash(i) * 14));
    spots(10, 31, CELL * 1.1, (x, y, i) => palm(c, x, y, 36 + hash(i) * 14, i));
    spots(3, 41, CELL * 1.5, (x, y) => pyramid(c, x, y, 54));
  } else if (id === "vinter") {
    spots(22, 21, CELL * 0.9, (x, y, i) => pine(c, x, y, 40 + hash(i) * 22));
    spots(5, 31, CELL * 0.9, (x, y) => snowman(c, x, y, 46));
    spots(9, 41, CELL * 0.8, (x, y, i) => iceCrystal(c, x, y, 22 + hash(i) * 12));
  } else if (id === "vulkan") {
    spots(14, 21, CELL * 0.85, (x, y, i) => volcanoRock(c, x, y, 14 + hash(i) * 10));
    spots(6, 31, CELL * 0.9, (x, y, i) => { c.fillStyle = "#ff7a1f"; c.beginPath(); c.arc(x, y, 6 + hash(i) * 5, 0, 7); c.fill(); c.fillStyle = "#ffe066"; c.beginPath(); c.arc(x, y, 3, 0, 7); c.fill(); });
  } else {
    spots(14, 21, CELL * 0.85, (x, y, i) => lollipop(c, x, y, 34 + hash(i) * 18, i));
    spots(7, 31, CELL * 0.85, (x, y) => candyCane(c, x, y, 38));
    spots(9, 41, CELL * 0.8, (x, y, i) => cupcake(c, x, y, 28 + hash(i) * 10, i));
  }
  startGate(c, geo.pts[0]![1], id);
  const last = map.waypoints[map.waypoints.length - 1]!;
  goal(c, last[0], last[1], id);
  // gentle light and vignette
  const sun = c.createRadialGradient(0, 0, 0, 0, 0, W * 0.8); sun.addColorStop(0, "rgba(255,255,230,.26)"); sun.addColorStop(1, "rgba(255,255,230,0)");
  c.fillStyle = sun; c.fillRect(0, 0, W, H);
  const vig = c.createRadialGradient(W / 2, H / 2, H * 0.5, W / 2, H / 2, W * 0.78); vig.addColorStop(0, "rgba(0,0,0,0)"); vig.addColorStop(1, "rgba(0,0,0,.24)");
  c.fillStyle = vig; c.fillRect(0, 0, W, H);
}
